/**
 * Which gamebook row to centre on load / on every anchor change. `AnchorGamebookTable.tsx` does
 * the actual DOM scroll (`scrollIntoView({ block: "center" })`) — this decides WHICH uid it
 * targets, as a pure function per this repo's no-RTL convention.
 */

export interface ScrollOrderEntry {
  anchorId: number;
  /** This entry's resolved uid, or `null` if not yet resolved — same shape `checkOrder.ts`'s
   *  `OrderCheckEntry` uses, so a caller that already built one for the order check can reuse it
   *  here too. */
  uid: string | null;
}

/**
 * The suggested play scrolls to centre when there IS one (`spotterPickUid`, trimmed non-empty).
 * When an anchor has NO suggestion at all, the decided behaviour is: centre on the nearest
 * RESOLVED neighbour's play instead (by `anchorId` distance, earlier neighbour wins a tie) — that
 * neighbour is exactly the play this anchor's own pick has to fall between (`checkOrder.ts`'s own
 * ordering rule), so it's the most useful place to land a reviewer who has to find the right row
 * by eye. `null` (scroll to the top of the list instead) only when NOTHING in the job is resolved
 * yet — there is no better anchor to centre on.
 */
export function resolveScrollTargetUid(
  currentAnchorId: number,
  spotterPickUid: string,
  orderedEntries: ScrollOrderEntry[]
): string | null {
  const trimmed = spotterPickUid.trim();
  if (trimmed) {
    return trimmed;
  }
  const resolvedOthers = orderedEntries.filter(
    (e) => e.anchorId !== currentAnchorId && e.uid
  );
  if (resolvedOthers.length === 0) {
    return null;
  }
  resolvedOthers.sort((a, b) => {
    const distance =
      Math.abs(a.anchorId - currentAnchorId) -
      Math.abs(b.anchorId - currentAnchorId);
    return distance !== 0 ? distance : a.anchorId - b.anchorId;
  });
  return resolvedOthers[0].uid;
}
